import { useState } from 'react';
import { Modal, Button } from '../ui';
import { useReview } from '../../hooks/useReview';

/**
 * Star Rating Input Component
 */
function StarRating({ value, onChange, disabled }) {
  const [hoverValue, setHoverValue] = useState(0);

  return (
    <div className="flex gap-2 justify-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHoverValue(star)}
          onMouseLeave={() => setHoverValue(0)}
          disabled={disabled}
          className="p-1 transition-all hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            className={`w-10 h-10 transition-colors ${
              (hoverValue || value) >= star
                ? 'text-accent fill-accent'
                : 'text-cream/20 fill-transparent stroke-current'
            }`}
            viewBox="0 0 24 24"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        </button>
      ))}
    </div>
  );
}

/**
 * Review Modal Component
 *
 * Allows users to leave a review after handover is complete.
 */
export default function ReviewModal({
  isOpen,
  onClose,
  raffleId,
  revieweeId,
  revieweeName,
  isReviewingAsSeller, // true if user is seller reviewing winner
}) {
  const { submitReview, submitting, error } = useReview(raffleId, revieweeId);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) return;

    const result = await submitReview(rating, text);
    if (result) {
      setSuccess(true);
      // Auto-close after showing success
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setRating(0);
      setText('');
      setSuccess(false);
      onClose();
    }
  };

  const roleLabel = isReviewingAsSeller ? 'winner' : 'seller';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Leave a Review" dark>
      {success ? (
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg
              className="w-10 h-10 text-success"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-display font-bold text-cream mb-2">
            Thanks for your review!
          </h3>
          <p className="text-cream/60">
            Your feedback helps build trust in the community.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header */}
          <div className="text-center pb-2">
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⭐</span>
            </div>
            <p className="text-cream/70 text-lg">
              How was your experience with <span className="text-cream font-medium">{revieweeName}</span>?
            </p>
          </div>

          {/* Star Rating */}
          <div className="py-4">
            <StarRating
              value={rating}
              onChange={setRating}
              disabled={submitting}
            />
            <p className="text-center mt-3 text-sm font-medium text-cream/60 h-5">
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
            </p>
          </div>

          {/* Optional Comment */}
          <div>
            <label className="block text-sm font-medium text-cream/70 mb-2">
              Add a comment <span className="text-cream/40">(optional)</span>
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Share your experience with this ${roleLabel}...`}
              rows={3}
              maxLength={500}
              disabled={submitting}
              className="w-full bg-midnight-lighter border border-cream/10 rounded-xl px-4 py-3 text-cream placeholder-cream/30 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none transition-colors"
            />
            <p className="text-xs text-cream/40 mt-1.5 text-right">
              {text.length}/500
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-error/10 border border-error/30 rounded-xl p-4">
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
              fullWidth
            >
              Skip
            </Button>
            <Button
              type="submit"
              disabled={rating === 0 || submitting}
              loading={submitting}
              fullWidth
            >
              Submit Review
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/**
 * Review Prompt Component
 *
 * Shows a prompt to leave a review after handover is complete.
 * Only shows if user hasn't already reviewed.
 */
export function ReviewPrompt({
  raffleId,
  revieweeId,
  revieweeName,
  isReviewingAsSeller,
  onReviewClick,
}) {
  const { hasReviewed, loading } = useReview(raffleId, revieweeId);

  // Don't render if missing required data, still loading, or already reviewed
  if (!raffleId || !revieweeId || loading || hasReviewed) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-accent/20 to-accent/10 border border-accent/30 rounded-xl p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center shrink-0">
            <span className="text-2xl">⭐</span>
          </div>
          <div>
            <h4 className="font-display font-bold text-cream">Leave a Review</h4>
            <p className="text-sm text-cream/60">
              How was your experience with {revieweeName}?
            </p>
          </div>
        </div>
        <Button variant="gold" size="sm" onClick={onReviewClick}>
          Review
        </Button>
      </div>
    </div>
  );
}
